import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { PaginatedResponse } from '../../shared/types';
import {
  AdminBankAccount,
  BankAccountStatus,
  approveBankAccount,
  listBankAccounts,
  rejectBankAccount,
} from './bankAccountsApi';
import { useAuth } from '../auth/AuthContext';

const STATUS_BADGE: Record<BankAccountStatus, string> = {
  pending: 'badge-warning',
  approved: 'badge-success',
  rejected: 'badge-danger',
};

const BankAccounts: React.FC = () => {
  const { hasPermission } = useAuth();
  const [accounts, setAccounts] = useState<PaginatedResponse<AdminBankAccount> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<BankAccountStatus | ''>('pending');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [rejecting, setRejecting] = useState<AdminBankAccount | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  const canApprove = hasPermission('kyc.approve');

  const load = useCallback(async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listBankAccounts({
        page,
        per_page: 20,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setAccounts(data);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load bank accounts'
        : 'Failed to load bank accounts';
      setError(msg);
      setAccounts(null);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    load(currentPage);
  }, [currentPage, load]);

  const applySearch = () => {
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const handleApprove = async (a: AdminBankAccount) => {
    if (!window.confirm(`Approve bank account "${a.bank_name} · ${a.account_number}"?`)) return;
    try {
      await approveBankAccount(a.id);
      load(currentPage);
    } catch {
      window.alert('Failed to approve');
    }
  };

  const openReject = (a: AdminBankAccount) => {
    setRejecting(a);
    setRejectReason('');
    setActionError('');
  };

  const handleReject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejecting) return;
    setIsSubmitting(true);
    setActionError('');
    try {
      await rejectBankAccount(rejecting.id, rejectReason.trim());
      setRejecting(null);
      load(currentPage);
    } catch (err: unknown) {
      const resp = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string; message?: string } } }).response
        : undefined;
      setActionError(resp?.data?.error || resp?.data?.message || 'Failed to reject');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">Bank Accounts</h1>
          <p className="text-slate-400 mt-1">Review user bank accounts used for receiving P2P payments</p>
        </div>

        <div className="card p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as BankAccountStatus | ''); setCurrentPage(1); }}
              className="input py-2"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applySearch())}
                placeholder="Name, username or email"
                className="input py-2 w-64"
              />
              <button type="button" onClick={applySearch} className="btn btn-secondary py-2">
                Apply
              </button>
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
                  className="btn btn-secondary py-2"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !accounts?.data.length ? (
            <div className="p-12 text-center text-slate-400">
              No bank accounts found.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">User</th>
                      <th className="table-header">Method / Bank</th>
                      <th className="table-header">Account</th>
                      <th className="table-header">Holder</th>
                      <th className="table-header">Currency</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Added</th>
                      {canApprove && <th className="table-header text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {accounts.data.map((a) => (
                      <tr key={a.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{a.id}</td>
                        <td className="table-cell text-slate-300">
                          <div>{a.user_name || `User #${a.user_id}`}</div>
                          <div className="text-xs text-slate-500">{a.user_email}</div>
                        </td>
                        <td className="table-cell text-white">
                          {a.bank_name}
                          {a.payment_method_code && (
                            <div className="text-xs text-slate-500 font-mono">{a.payment_method_code}</div>
                          )}
                        </td>
                        <td className="table-cell font-mono text-slate-300">{a.account_number}</td>
                        <td className="table-cell text-slate-300">{a.account_holder_name}</td>
                        <td className="table-cell text-slate-300">{a.currency}</td>
                        <td className="table-cell">
                          <span className={`badge ${STATUS_BADGE[a.status] ?? 'badge-warning'}`}>{a.status}</span>
                          {a.status === 'rejected' && a.rejection_reason && (
                            <div className="text-xs text-slate-500 mt-1 max-w-[200px] truncate" title={a.rejection_reason}>
                              {a.rejection_reason}
                            </div>
                          )}
                        </td>
                        <td className="table-cell text-slate-400">{new Date(a.created_at).toLocaleDateString()}</td>
                        {canApprove && (
                          <td className="table-cell text-right">
                            {a.status === 'pending' && (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleApprove(a)}
                                  className="px-3 py-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-all"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => openReject(a)}
                                  className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all"
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {accounts.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={accounts.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={!!rejecting}
        onClose={() => setRejecting(null)}
        title={`Reject bank account #${rejecting?.id ?? ''}`}
      >
        <form onSubmit={handleReject} className="space-y-4">
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Reason *</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="input min-h-[100px]"
              placeholder="Why is this account rejected?"
              required
              maxLength={500}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setRejecting(null)} className="flex-1 btn btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !rejectReason.trim()}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isSubmitting ? 'Rejecting...' : 'Reject'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};

export default BankAccounts;
